import React, { useEffect, useMemo, useRef, useState, type ComponentProps } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DualListBox from "react-dual-listbox";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { dualListBoxIcons } from "./ui-components/SXDualListBoxIcons";
import { selectAllSections } from "../features/materialsSections/materialSectionSlice";
import { selectToken } from "../features/user/userSlice";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { ProjectSlice, selectProject } from "../features/projects/projectSlice";
import { ng } from "@common/ngds";
import {
  MAX_DESIGN_SECTIONS,
  collectDesignDatabases,
  flattenSectionFamilies,
  getCachedAutoDesignSettings,
  getDesignSectionFamilies,
  getElementGuid,
  getElementMaterial,
  getElementMaterialGuid,
  getElementSectionGuid,
  getTimberProductType,
  getTimberSectionFamilies,
  hasFireProtectionMaterial,
  hasSteelFireProtectionTemperature,
  parseSectionDimensions,
  type DesignSectionItem,
  type RunMemberDesign,
} from "../helpers/memberDesign";
import { RATIO_TO_PERCENT_UNIT, DesignRunButton } from "./SXMemberDesignShared";

import "react-dual-listbox/lib/react-dual-listbox.css";

interface SteelTimberDesignSettingsProps {
  group: "Steel" | "Timber";
  isDesigning: boolean;
  runDesign: RunMemberDesign;
}

// Fallback size range for timber, used only when neither the previous run nor the element's current
// section gives anything to seed from (i.e. an unparseable section name).
const FALLBACK_TIMBER_SCOPE = { minY: 100, maxY: 300, minZ: 100, maxZ: 600 };

// Round a seeded bound to a whole 10 mm so the fields read like something a user would have typed.
const roundTo10 = (value: number) => Math.max(0, Math.round(value / 10) * 10);

// Fire protection thickness span the design may pick from, and its increment — the same values a
// project is created with (see projGen). In mm, as the fields show them; the payload carries meters.
const DEFAULT_FIRE_THICKNESS_MM = { min: 1, max: 10, step: 1 };

// The increment a steel member protected to a maximum temperature — rather than by a material — has
// that temperature designed with. In °C, both as the field shows it and as the payload carries it.
const DEFAULT_FIRE_TEMPERATURE_STEP_C = 10;

/**
 * Auto-design settings for a steel or timber column. The design process for these is the same as
 * for the corresponding beams in sx-beam: the only inputs are the candidate sections and the target
 * utilization and, where the member is fire protected, the span its protection is designed within —
 * everything else the design service derives from the element itself. A steel member protected to a
 * maximum temperature has no protection thickness, so what is designed there is the temperature
 * step instead.
 *
 * The two materials differ only in how sections are chosen:
 *  - Steel: rolled profile names ("HEA 220") do not encode their dimensions, so a size range would
 *    filter on the wrong numbers. Sections are picked explicitly, one family at a time, in an
 *    Available/Chosen pair of list boxes that takes a whole selection at once.
 *  - Timber: section names *are* the dimensions, so a size range maps onto the library directly,
 *    exactly as for concrete. Which families the range may draw from is decided by the element's
 *    material — see getTimberSectionFamilies.
 */
export const SteelTimberDesignSettings = ({ group, isDesigning, runDesign }: SteelTimberDesignSettingsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const allSections = useAppSelector(selectAllSections) as Record<string, unknown>;
  const token = useAppSelector(selectToken);
  const selProject = useAppSelector(selectProject);

  const [limitUtilization, setLimitUtilization] = useState(1);
  // Steel: the family whose sections are listed for picking, and the explicit picks (across all
  // families, so switching family does not discard what is already chosen).
  const [selectedFamily, setSelectedFamily] = useState<string>("");
  const [pickedGuids, setPickedGuids] = useState<string[]>([]);
  // Set when a move brought over more sections than a run may span, so the picks that did not fit
  // are not dropped silently. Cleared by the next move that stays within the cap.
  const [capNotice, setCapNotice] = useState<string | null>(null);
  // Timber: the size range the candidate sections are derived from.
  const [minY, setMinY] = useState(FALLBACK_TIMBER_SCOPE.minY);
  const [maxY, setMaxY] = useState(FALLBACK_TIMBER_SCOPE.maxY);
  const [minZ, setMinZ] = useState(FALLBACK_TIMBER_SCOPE.minZ);
  const [maxZ, setMaxZ] = useState(FALLBACK_TIMBER_SCOPE.maxZ);
  // Fire protection: the thickness span, and the increment within it, the design may choose the
  // protection thickness from. Held in mm, converted to meters when the payload is assembled.
  const [thicknessMin, setThicknessMin] = useState(DEFAULT_FIRE_THICKNESS_MM.min);
  const [thicknessMax, setThicknessMax] = useState(DEFAULT_FIRE_THICKNESS_MM.max);
  const [thicknessStep, setThicknessStep] = useState(DEFAULT_FIRE_THICKNESS_MM.step);
  // Steel protected to a maximum temperature: the increment that temperature is designed with.
  const [temperatureStep, setTemperatureStep] = useState(DEFAULT_FIRE_TEMPERATURE_STEP_C);

  const hasRestoredUtilizationRef = useRef(false);
  // What the section choice was last seeded for. Steel and timber share this component, and a timber
  // material change swaps whole section families in and out, so a choice seeded for the previous
  // material is meaningless — seed again rather than leave the user with an empty result to explain.
  const seededForRef = useRef<string | null>(null);
  // Steel and timber keep their fire settings in different branches of the cached AutoDesign, so the
  // fire fields are seeded per material as well.
  const seededFireForRef = useRef<string | null>(null);

  const cachedSettings = getCachedAutoDesignSettings(selProject);
  const activeSectionGuid = getElementSectionGuid(selProject?.data);

  // Timber materials and timber sections are not freely combinable, so the element's material
  // decides which families may take part. Steel has no such restriction.
  const timberProduct = getTimberProductType(selProject?.data);
  const allowedFamilies = group === "Timber" ? getTimberSectionFamilies(timberProduct) : undefined;

  // The protection thickness limits only mean something for a member that has a protection material
  // to design, and the temperature step only for a steel member protected to a temperature. Each
  // set of fields appears only for the protection it belongs to; an unprotected member gets neither.
  const hasProtectionMaterial = hasFireProtectionMaterial(selProject?.data, group);
  const hasProtectionTemperature = hasSteelFireProtectionTemperature(selProject?.data, group);

  const families = useMemo(
    () => getDesignSectionFamilies(allSections, group, allowedFamilies),
    // allowedFamilies is derived from the material, so the product type is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allSections, group, timberProduct],
  );
  // The library arrives with a fetch the panel kicks off on mount, so "no families" means "not yet"
  // until it has landed — without this an empty library reads as an unsupported material.
  const isSectionLibraryLoaded = Object.keys(allSections ?? {}).length > 0;
  const familyNames = useMemo(() => Object.keys(families).sort(), [families]);
  const sectionItems = useMemo(() => flattenSectionFamilies(families), [families]);
  const sectionByGuid = useMemo(() => new Map(sectionItems.map((section) => [section.guid, section])), [sectionItems]);

  // Restore the target utilization from the previous run once the project has loaded.
  useEffect(() => {
    if (hasRestoredUtilizationRef.current) return;
    const cachedLimit = cachedSettings?.limitUtilization;
    if (typeof cachedLimit !== "number" || !Number.isFinite(cachedLimit)) return;
    hasRestoredUtilizationRef.current = true;
    setLimitUtilization(cachedLimit);
  }, [cachedSettings?.limitUtilization]);

  // The previous run's fire protection limits, from the branch of the cached settings this material
  // stores them in.
  const cachedProtectingMaterial =
    group === "Steel"
      ? cachedSettings?.steel?.fireSettings?.protectingMaterial
      : cachedSettings?.timber?.fireSettings?.protectingMaterial;
  // Only steel can be designed against a temperature, and it shares a oneof with the thickness
  // span, so a run stores one arm or the other — never both.
  const cachedTemperatureStep = group === "Steel" ? cachedSettings?.steel?.fireSettings?.temperatureStep : undefined;

  // Restore those limits once the project has loaded. Zero is what an uninitialised
  // ProtectingMaterial carries, so it reads as "nothing was stored" rather than as a limit that
  // would leave the design no thickness to choose. The arm the previous run did not store simply
  // keeps its default, which is what a member switched between the two protections gets.
  useEffect(() => {
    if (seededFireForRef.current === group) return;
    if (!cachedProtectingMaterial && cachedTemperatureStep == null) return;
    seededFireForRef.current = group;

    // Thicknesses are stored in meters and shown in mm; the temperature step is °C either way.
    const seed = (value: unknown, scale: number, apply: (value: number) => void) => {
      const scaled = Number(value) * scale;
      if (Number.isFinite(scaled) && scaled > 0) apply(scaled);
    };
    if (cachedProtectingMaterial) {
      seed(cachedProtectingMaterial.thicknessMin, 1000, setThicknessMin);
      seed(cachedProtectingMaterial.thicknessMax, 1000, setThicknessMax);
      seed(cachedProtectingMaterial.thicknessStep, 1000, setThicknessStep);
    }
    seed(cachedTemperatureStep, 1, setTemperatureStep);
  }, [group, cachedProtectingMaterial, cachedTemperatureStep]);

  // Seed the section choice from the previous run, once the section library is available. Guids the
  // library no longer offers are dropped — after a timber material change the sections cached from
  // the previous run can belong to a family this material may not be paired with.
  const seedKey = `${group}:${timberProduct}`;
  useEffect(() => {
    if (seededForRef.current === seedKey) return;
    if (sectionItems.length === 0) return;
    seededForRef.current = seedKey;

    const cachedGuids = (cachedSettings?.sectionGuids ?? []).filter((guid) => sectionByGuid.has(guid));

    if (group === "Steel") {
      setPickedGuids(cachedGuids);
      return;
    }

    // Timber: the size range is not part of the AutoDesign settings, so it cannot be restored
    // directly. Seed it to the range that brackets the previous run's sections, which reproduces
    // that selection exactly; failing that, to a range around the column's current section.
    const cachedDims = cachedGuids
      .map((guid) => parseSectionDimensions(sectionByGuid.get(guid)?.name ?? ""))
      .filter((dims): dims is { y: number; z: number } => dims !== null);

    if (cachedDims.length > 0) {
      setMinY(Math.min(...cachedDims.map((d) => d.y)));
      setMaxY(Math.max(...cachedDims.map((d) => d.y)));
      setMinZ(Math.min(...cachedDims.map((d) => d.z)));
      setMaxZ(Math.max(...cachedDims.map((d) => d.z)));
      return;
    }

    const currentDims = parseSectionDimensions(sectionByGuid.get(activeSectionGuid)?.name ?? "");
    if (currentDims) {
      setMinY(roundTo10(currentDims.y * 0.5));
      setMaxY(roundTo10(currentDims.y * 1.5));
      setMinZ(roundTo10(currentDims.z * 0.5));
      setMaxZ(roundTo10(currentDims.z * 1.5));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey, sectionItems, sectionByGuid, cachedSettings?.sectionGuids, activeSectionGuid]);

  // The family whose sections are listed. Derived rather than repaired in an effect, so it cannot go
  // stale against a library that has just changed (a timber material switch drops whole families,
  // and the steel library only arrives with the first fetch): the user's click is honoured for as
  // long as it still names a family, and otherwise the run's — or the column's own — family opens.
  const openFamily = useMemo(() => {
    if (selectedFamily && familyNames.includes(selectedFamily)) return selectedFamily;
    const preferred =
      sectionByGuid.get(pickedGuids[0] ?? "")?.familyName ?? sectionByGuid.get(activeSectionGuid)?.familyName;
    if (preferred && familyNames.includes(preferred)) return preferred;
    return familyNames[0] ?? "";
  }, [selectedFamily, familyNames, sectionByGuid, pickedGuids, activeSectionGuid]);

  // Steel picker options. Every family's sections are offered — the picks are what the "Chosen" box
  // lists, and those span families — while `availableGuids` narrows the "Available" box to the open
  // family. Ordered by family so a choice made across several of them still reads grouped.
  const sectionOptions = useMemo(
    () =>
      familyNames.flatMap((familyName) =>
        families[familyName].map((section) => ({ value: section.guid, label: section.name })),
      ),
    [familyNames, families],
  );
  const availableGuids = useMemo(
    () => (families[openFamily] ?? []).map((section) => section.guid),
    [families, openFamily],
  );

  // Drop picks the library no longer offers, so a stale guid can never reach a design run.
  useEffect(() => {
    if (sectionItems.length === 0) return;
    setPickedGuids((previous) => {
      const kept = previous.filter((guid) => sectionByGuid.has(guid));
      return kept.length === previous.length ? previous : kept;
    });
  }, [sectionItems, sectionByGuid]);

  // Timber's candidates are derived from the size range; steel's are the explicit picks.
  const timberMatches = useMemo<DesignSectionItem[]>(() => {
    if (group !== "Timber") return [];
    return sectionItems.filter((section) => {
      const dims = parseSectionDimensions(section.name);
      if (!dims) return false;
      return dims.y >= minY && dims.y <= maxY && dims.z >= minZ && dims.z <= maxZ;
    });
  }, [group, sectionItems, minY, maxY, minZ, maxZ]);

  const designSectionGuids = useMemo(
    () => (group === "Steel" ? pickedGuids : timberMatches.map((section) => section.guid)),
    [group, pickedGuids, timberMatches],
  );

  // A run needs at least one candidate, and no more than the design service is exercised with.
  const blockedReason = useMemo(() => {
    if (!isSectionLibraryLoaded) {
      return t("loading_.label", "Loading");
    }
    // No families at all means the material has nothing it may be paired with — for timber, a
    // product type outside the section library (see getTimberSectionFamilies). Widening the size
    // range would not help, so say what is actually wrong.
    if (familyNames.length === 0) {
      return t("design_material_unsupported_.label", "Member design is not available for this material.");
    }
    if (designSectionGuids.length === 0) {
      return t("err_no_design_sections_.label", "At least one cross-section needs to be selected for the design.");
    }
    if (designSectionGuids.length > MAX_DESIGN_SECTIONS) {
      return t(
        "MSG_too_many_matching_sections_.label",
        `The size limits match ${designSectionGuids.length} sections. Narrow them so that at most ${MAX_DESIGN_SECTIONS} remain.`,
        { matched: designSectionGuids.length, max: MAX_DESIGN_SECTIONS },
      );
    }
    return null;
  }, [isSectionLibraryLoaded, familyNames.length, designSectionGuids.length, t]);

  // The picker moves whole selections at a time, so one move can ask for more sections than a run
  // may span. Take what fits rather than refusing the move — what is already chosen is never
  // displaced by it, and the notice says why the rest stayed behind.
  const handleSectionsChange = (next: string[]) => {
    if (next.length <= MAX_DESIGN_SECTIONS) {
      setPickedGuids(next);
      setCapNotice(null);
      return;
    }
    const kept = next.filter((guid) => pickedGuids.includes(guid));
    const added = next.filter((guid) => !pickedGuids.includes(guid));
    setPickedGuids([...kept, ...added.slice(0, Math.max(0, MAX_DESIGN_SECTIONS - kept.length))]);
    setCapNotice(
      t(
        "MSG_design_sections_capped_.label",
        `At most ${MAX_DESIGN_SECTIONS} sections can take part in a run — the ones beyond that were not added.`,
        { max: MAX_DESIGN_SECTIONS },
      ),
    );
  };

  const buildAutoDesign = async () => {
    const sectionGuids = designSectionGuids;
    const materialGuid = getElementMaterialGuid(selProject?.data);

    const { secDb, mtrlDb } = await collectDesignDatabases({
      project: selProject,
      token,
      sectionGuids,
      // Steel and timber auto-design does not vary the material — the design service only needs the
      // one the element already references, resolved so it carries its properties.
      materialGuids: materialGuid ? [materialGuid] : [],
    });

    const settingsObj = ng.endpoint_args.common_api_functions.AutoDesignSettings.create({
      elementGuid: getElementGuid(selProject?.data),
      sectionGuids,
      limitUtilization,
      column: ng.endpoint_args.common_api_functions.AutoDesignColumn.create({}),
    });

    // The thickness span a protected member's protection is designed within — the panel's to own,
    // in meters here. Sent whether or not the fields were shown: a member with no protection
    // material has no thickness to design, so the span is simply unused, and it survives protection
    // being switched off and on again. Everything else in the material's settings is carried
    // forward from the previous run, so nothing the server put there is lost. The bounds are
    // ordered on the way out: a min above the max would otherwise reach the design service as an
    // empty range.
    const protectingMaterial = () =>
      ng.endpoint_args.common_api_functions.ProtectingMaterial.create({
        thicknessStep: thicknessStep / 1000,
        thicknessMin: Math.min(thicknessMin, thicknessMax) / 1000,
        thicknessMax: Math.max(thicknessMin, thicknessMax) / 1000,
      });

    if (group === "Steel") {
      const steel = cachedSettings?.steel
        ? ng.endpoint_args.common_api_functions.AutoDesignSteel.fromObject(cachedSettings.steel)
        : ng.endpoint_args.common_api_functions.AutoDesignSteel.create({});
      const fireSettings = steel.fireSettings ?? ng.endpoint_args.common_api_functions.AutoDesignSteelFire.create({});
      // Steel's protectingMaterial shares a oneof with temperatureStep, so only the arm matching how
      // the member is actually protected can be sent — the design varies the quantity the member
      // has. A member protected to a maximum temperature has no thickness to design, and one
      // protected by a material has no temperature to step through.
      if (hasProtectionTemperature) {
        fireSettings.setTemperatureStep(temperatureStep);
      } else {
        fireSettings.setProtectingMaterial(protectingMaterial());
      }
      steel.fireSettings = fireSettings;
      settingsObj.setSteel(steel);
    } else {
      const timber = cachedSettings?.timber
        ? ng.endpoint_args.common_api_functions.AutoDesignTimber.fromObject(cachedSettings.timber)
        : ng.endpoint_args.common_api_functions.AutoDesignTimber.create({});
      const fireSettings = timber.fireSettings ?? ng.endpoint_args.common_api_functions.AutoDesignTimberFire.create({});
      fireSettings.protectingMaterial = protectingMaterial();
      timber.fireSettings = fireSettings;
      settingsObj.setTimber(timber);
    }

    const autoDesign = ng.endpoint_args.common_api_functions.AutoDesign.create({
      active: true,
      settings: [settingsObj],
      secDb: secDb.map((s) => ng.sections.Section.fromObject(s)),
      mtrlDb: mtrlDb.map((m) => ng.material.Data.fromObject(m)),
    });

    return ng.endpoint_args.common_api_functions.AutoDesign.toObject(autoDesign);
  };

  const materialName = getElementMaterial(selProject?.data)?.id?.name ?? "";

  const utilizationField = (
    <>
      <Row style={{ marginTop: 16, marginBottom: 0, borderBottom: "1px solid lightgray" }}>
        <Col className="header sx-cd-section-title">
          <span className="header">{t("utilisation_.label")}</span>
        </Col>
      </Row>
      <Row className="container-row align-items-center">
        <Col className="editfield-label">{t("limit_utilization_.label")} [%]</Col>
        <Col xs={12} className="px-0">
          <SXEditField
            type="number"
            val={limitUtilization}
            unit={RATIO_TO_PERCENT_UNIT}
            displayUnit={false}
            textAlign="left"
            fullWidth
            disabled={isDesigning}
            onUpdate={(value) => {
              // value arrives as a ratio — SXEditField has already applied fromInput.
              const ratio = Number(value);
              if (!Number.isFinite(ratio) || ratio < 0) return;
              setLimitUtilization(ratio);
              dispatch(ProjectSlice.actions.setAutoDesignLimitUtilization(ratio));
            }}
          />
        </Col>
      </Row>
    </>
  );

  // A bound or a step of zero gives the design service nothing to work with, so the field keeps its
  // previous value rather than accepting one.
  const updatePositiveValue = (value: unknown, revertValue: () => void, apply: (value: number) => void) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      revertValue();
      return;
    }
    apply(parsed);
  };

  const fireProtectionFields = (
    <>
      <Row style={{ marginTop: 16, marginBottom: 0, borderBottom: "1px solid lightgray" }}>
        <Col className="header sx-cd-section-title">
          <span className="header">
            {t("fire_protection_.label")} - {t("thickness_.label")}
          </span>
        </Col>
      </Row>

      {/* All three across one row: the header says what they are thicknesses of, and short labels
          keep each one on a single line at a third of the panel's width. */}
      <Row className="container-row" style={{ alignItems: "start" }}>
        <Col xs={4} className="ps-0">
          <div className="editfield-label">{t("min_.label")} (mm)</div>
          <div className="ps-0 px-0" style={{ height: 32 }}>
            <SXEditField
              id="sx-design-fire-thickness-min"
              type="number"
              val={thicknessMin}
              textAlign="left"
              fullWidth
              disableNegativeNumber
              disabled={isDesigning}
              onUpdate={(value, revertValue) => updatePositiveValue(value, revertValue, setThicknessMin)}
            />
          </div>
        </Col>
        <Col xs={4}>
          <div className="editfield-label">{t("max_.label")} (mm)</div>
          <div className="ps-0 px-0" style={{ height: 32 }}>
            <SXEditField
              id="sx-design-fire-thickness-max"
              type="number"
              val={thicknessMax}
              textAlign="left"
              fullWidth
              disableNegativeNumber
              disabled={isDesigning}
              onUpdate={(value, revertValue) => updatePositiveValue(value, revertValue, setThicknessMax)}
            />
          </div>
        </Col>
        <Col xs={4} className="px-0">
          <div className="editfield-label">{t("step_.label")} (mm)</div>
          <div className="ps-0 px-0" style={{ height: 32 }}>
            <SXEditField
              id="sx-design-fire-thickness-step"
              type="number"
              val={thicknessStep}
              textAlign="left"
              fullWidth
              disableNegativeNumber
              disabled={isDesigning}
              onUpdate={(value, revertValue) => updatePositiveValue(value, revertValue, setThicknessStep)}
            />
          </div>
        </Col>
      </Row>
    </>
  );

  const fireTemperatureFields = (
    <>
      <Row>
        <Col xs={11} style={{ marginBottom: 5 }} className="ps-0">
          <span className="header">
            {t("fire_protection_.label")} - {t("temperature_.label")}
          </span>
        </Col>
      </Row>

      {/* A member checked against a maximum temperature carries no protection thickness, so the one
          thing a run has to vary is the increment it searches that temperature with. Laid out in the
          same third-width column the thickness fields use, so the two read as the same row. */}
      <Row className="container-row" style={{ alignItems: "start" }}>
        <Col xs={4} className="ps-0">
          <div className="editfield-label">{t("step_.label")} (°C)</div>
          <div className="ps-0 px-0" style={{ height: 32 }}>
            <SXEditField
              id="sx-design-fire-temperature-step"
              type="number"
              val={temperatureStep}
              textAlign="left"
              fullWidth
              disableNegativeNumber
              disabled={isDesigning}
              onUpdate={(value, revertValue) => updatePositiveValue(value, revertValue, setTemperatureStep)}
            />
          </div>
        </Col>
      </Row>
    </>
  );

  // `lang` only takes strings, so the cap has to be part of the "Chosen" header rather than a node
  // beside it — which is where it belongs anyway, as it is that box that is capped.
  const sectionPickerLang: NonNullable<ComponentProps<typeof DualListBox>["lang"]> = {
    availableHeader: t("available_.label", "Available"),
    selectedHeader: `${t("chosen_.label", "Chosen")} (${pickedGuids.length} / ${MAX_DESIGN_SECTIONS})`,
    noAvailableOptions: t("no_matching_sections_.label", "No matching sections"),
    noSelectedOptions: t("no_sections_chosen_.label", "No sections chosen"),
  };

  const steelSectionPicker = (
    <>
      <Row style={{ marginTop: 16, marginBottom: 0, borderBottom: "1px solid lightgray" }}>
        <Col className="header sx-cd-section-title">
          <span className="header">{t("sections_.label", "Sections")}</span>
        </Col>
      </Row>
      <Row className="sx-cd-picker">
        <Col xs={3} className="ps-0">
          <div className=" sx-cd-subtitle">{t("type_.label", "Type")}</div>
          <ul className="sx-cd-family-list">
            {familyNames.map((familyName) => {
              const pickedInFamily = families[familyName].filter((section) =>
                pickedGuids.includes(section.guid),
              ).length;
              return (
                <li
                  key={familyName}
                  id={`sx-design-family-${familyName}`}
                  className={familyName === openFamily ? "is-active" : ""}
                  onClick={() => setSelectedFamily(familyName)}
                >
                  <span>{familyName}</span>
                  {pickedInFamily > 0 && <span className="sx-cd-family-count">{pickedInFamily}</span>}
                </li>
              );
            })}
          </ul>
        </Col>
        {/* Two list boxes rather than a grid of toggles: these are native multi-selects, so a run of
            sections is a click and a shift-click apart instead of one click each. The type column to
            the left is what narrows "Available"; "Chosen" keeps showing the picks from every family,
            which the counter alone could not. */}
        <Col xs={9} className="px-0">
          <DualListBox<string>
            id="sx-design-sections"
            className="sx-dual-listbox sx-cd-section-listbox"
            icons={dualListBoxIcons}
            lang={sectionPickerLang}
            showHeaderLabels
            showNoOptionsText
            disabled={isDesigning}
            options={sectionOptions}
            available={availableGuids}
            selected={pickedGuids}
            onChange={handleSectionsChange}
          />
        </Col>
      </Row>
      {capNotice && (
        <Row>
          <Col xs={12} className="px-0">
            <div className="sx-cd-cap-note">{capNotice}</div>
          </Col>
        </Row>
      )}
    </>
  );

  const timberSectionScope = (
    <>
      <Row style={{ marginTop: 16, marginBottom: 0, borderBottom: "1px solid lightgray" }}>
        <Col className="header sx-cd-section-title">
          <span className="header">{t("column_size_limits_.label", "Column size limits")}</span>
        </Col>
      </Row>

      <Row className="container-row">
        <Col className="editfield-label">{t("min_column_size_y_mm_.label", "Min column size y (mm)")}</Col>
        <Col xs={12} className="d-flex align-items-center gap-2 ps-0 px-0" style={{ height: 32 }}>
          <SXEditField
            type="number"
            val={minY}
            textAlign="left"
            fullWidth
            disabled={isDesigning}
            onUpdate={(value) => setMinY(Number(value))}
          />
          <span className="sx-cd-range-separator">-</span>
          <SXEditField
            type="number"
            val={maxY}
            textAlign="left"
            fullWidth
            disabled={isDesigning}
            onUpdate={(value) => setMaxY(Number(value))}
          />
        </Col>
      </Row>

      <Row className="container-row">
        <Col className="editfield-label">{t("min_column_size_z_mm_.label", "Min column size z (mm)")}</Col>
        <Col xs={12} className="d-flex align-items-center gap-2 px-0" style={{ height: 32 }}>
          <SXEditField
            type="number"
            val={minZ}
            textAlign="left"
            fullWidth
            disabled={isDesigning}
            onUpdate={(value) => setMinZ(Number(value))}
          />
          <span className="sx-cd-range-separator">-</span>
          <SXEditField
            type="number"
            val={maxZ}
            textAlign="left"
            fullWidth
            disabled={isDesigning}
            onUpdate={(value) => setMaxZ(Number(value))}
          />
        </Col>
      </Row>

      <Row style={{ marginBottom: 8 }}>
        <Col className="editfield-label">
          {t("matching_sections_from_library_.label", "Matching sections from library")}
          {/* The families searched follow from the material: sawn/dressed lumber and glulam do not
              share materials, so only the compatible ones can appear here. */}
          {materialName && (
            <span className="sx-cd-scope-note">
              {" — "}
              {materialName}
              {familyNames.length > 0 && ` (${familyNames.join(", ")})`}
            </span>
          )}
        </Col>
        <Col xs={12} className="sx-cd-chip-wrap sx-cd-chip-wrap--display sx-cd-chip-scroll">
          {timberMatches.length > 0 ? (
            timberMatches.map((section) => (
              <span key={section.guid} className="sx-cd-chip sx-cd-chip--display" aria-hidden="true">
                {section.name}
              </span>
            ))
          ) : (
            <span className="sx-cd-no-match">{t("no_matching_sections_.label", "No matching sections")}</span>
          )}
        </Col>
      </Row>
    </>
  );

  return (
    <Container>
      {group === "Steel" ? steelSectionPicker : timberSectionScope}
      {utilizationField}
      {(hasProtectionMaterial || hasProtectionTemperature) && (
        <>{hasProtectionTemperature ? fireTemperatureFields : fireProtectionFields}</>
      )}
      <hr />
      <Row className="pb-4">
        <Col xs={12} className="ps-0 px-0">
          <DesignRunButton
            isDesigning={isDesigning}
            blockedReason={blockedReason}
            onRun={() => runDesign(buildAutoDesign)}
          />
        </Col>
      </Row>
    </Container>
  );
};
